import React, { useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RotateCcw, LayoutGrid, Trophy } from "lucide-react";
import { Screen } from "@/components/Screen";
import { ResultBadge, RESULT_META } from "@/components/ResultBadge";
import { useI18n } from "@/i18n/I18nProvider";
import { formatDate, todayLocalISO } from "@/lib/date";
import {
  getCheckInByDate,
  getLatestCheckIn,
} from "@/store/store";
import { generateDailyRecommendation } from "@/lib/recommendations";

export default function Result() {
  const { t, language } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();

  // Resolve which entry to show: prefer fresh submit → today's stored → latest.
  const entry = useMemo(() => {
    if (location.state?.entry) return location.state.entry;
    const today = getCheckInByDate(todayLocalISO());
    if (today) return today;
    return getLatestCheckIn();
  }, [location.state]);

  // If we have nothing to show, gently push the user back to /today.
  useEffect(() => {
    if (!entry) {
      navigate("/today", { replace: true });
    }
  }, [entry, navigate]);

  // On a fresh submit, confirm auto-save with a toast (once).
  const toastFiredRef = useRef(false);
  useEffect(() => {
    if (location.state?.fresh && !toastFiredRef.current) {
      toastFiredRef.current = true;
      toast.success(t("result.toastSaved"));
      // Clear the state so refreshes don't re-toast.
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, t]);

  if (!entry) return null;

  const meta = RESULT_META[entry.result] || RESULT_META.stay;
  const color = meta.color;
  const titleLabel = t(`result.labels.${entry.result}.title`);
  const description = t(`result.labels.${entry.result}.description`);
  // Recompute the recommendation in the current language so switching PL/EN
  // updates the text without needing a fresh check-in.
  const recommendation = generateDailyRecommendation(
    entry.result,
    entry.date,
    language,
  );

  const balancePercent = Math.max(0, Math.min(100, entry.balanceScore));

  return (
    <Screen testId="result-screen">
      <header className="pt-4">
        <p className="text-xs uppercase tracking-[0.18em] text-[#E8D8B8]/70">
          {t("result.eyebrow")}
        </p>
        <h1
          className="mt-2 text-sm text-[#94A3B8] capitalize"
          data-testid="result-date"
        >
          {formatDate(entry.date, language)}
        </h1>
      </header>

      {/* Main result card */}
      <section
        className="dc-card relative overflow-hidden mt-6 p-6"
        data-testid={`result-card-${entry.result}`}
      >
        <div
          className="absolute -top-16 -right-16 w-52 h-52 rounded-full pointer-events-none"
          style={{
            background: `radial-gradient(circle, ${color}22 0%, ${color}00 70%)`,
          }}
        />
        <div className="relative flex flex-col items-center text-center">
          <ResultBadge result={entry.result} size={112} />
          <h2
            className="mt-5 text-3xl sm:text-4xl font-medium leading-tight"
            style={{
              letterSpacing: "-0.025em",
              color: "#F8FAFC",
            }}
            data-testid="result-title"
          >
            {titleLabel}
          </h2>
          <p
            className="mt-3 text-[15px] text-[#CBD5E1] leading-relaxed max-w-[360px]"
            data-testid="result-description"
          >
            {description}
          </p>
        </div>
      </section>

      {/* Recommendation */}
      <section
        className="dc-card mt-4 p-5"
        data-testid="result-recommendation-card"
      >
        <p
          className="text-[11px] uppercase tracking-[0.16em] mb-2"
          style={{ color: `${color}CC` }}
        >
          {t("result.recommendationLabel")}
        </p>
        <p
          className="text-[15px] text-[#F8FAFC]/95 leading-relaxed"
          style={{ letterSpacing: "-0.005em" }}
          data-testid="result-recommendation"
        >
          {recommendation}
        </p>
      </section>

      {/* Daily win, if any */}
      {entry.dailyWin ? (
        <section
          className="dc-card mt-4 p-5"
          data-testid="result-daily-win-card"
        >
          <div className="flex items-center gap-2">
            <Trophy size={14} className="text-[#F4C95D]/80" strokeWidth={1.7} />
            <p className="text-[11px] uppercase tracking-[0.16em] text-[#E8D8B8]/80">
              {t("result.dailyWinLabel")}
            </p>
          </div>
          <p
            className="mt-2 text-[15px] text-[#F8FAFC]/95 leading-relaxed italic"
            style={{ letterSpacing: "-0.005em" }}
            data-testid="result-daily-win-value"
          >
            “{entry.dailyWin}”
          </p>
        </section>
      ) : null}

      {/* Balance score */}
      <section className="dc-card mt-4 p-5" data-testid="result-balance-card">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] uppercase tracking-[0.16em] text-[#E8D8B8]/80">
            {t("result.balanceLabel")}
          </p>
          <span
            className="text-lg font-medium tabular-nums"
            style={{ color, letterSpacing: "-0.01em" }}
            data-testid="result-balance-score"
          >
            {entry.balanceScore}
            <span className="text-xs text-white/40 ml-1">/100</span>
          </span>
        </div>
        <div
          className="h-2 rounded-full overflow-hidden"
          style={{ background: "rgba(255,255,255,0.06)" }}
          aria-label="Balance bar"
        >
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${balancePercent}%`,
              background: `linear-gradient(90deg, ${color}66, ${color})`,
            }}
          />
        </div>
      </section>

      {/* CTAs */}
      <div className="mt-8 space-y-3">
        <button
          type="button"
          onClick={() => navigate("/today", { state: { redo: true } })}
          data-testid="result-redo"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium text-white/85 border border-white/12 hover:bg-white/[0.05] hover:text-white transition-colors duration-200"
        >
          <RotateCcw size={15} strokeWidth={1.7} />
          {t("result.actions.redo")}
        </button>
        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          data-testid="result-goto-dashboard"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium text-white/70 hover:text-white transition-colors duration-200"
        >
          <LayoutGrid size={15} strokeWidth={1.7} />
          {t("result.actions.dashboard")}
        </button>
      </div>
    </Screen>
  );
}
